import { Router, Route } from "@solidjs/router";
import { lazy, Suspense, onMount, createEffect, ErrorBoundary } from "solid-js";
import ToastContainer from "./shared/components/Toast";
import SiteFooter from "./shared/components/SiteFooter";
// Not lazy: a first-time visitor must be asked before anything else happens, and a chunk that has
// yet to arrive cannot ask.
import ConsentBanner from "./tracking/components/ConsentBanner";
import { SessionExpiredNotice } from "./auth/SessionExpiredNotice";
import WhatsNewDialog from "./shared/components/WhatsNewDialog";
import { loadModule, t } from "./shared/i18n";
import { loadTenant, themeState } from "./shared/stores/themeStore";
import { loadSession } from "./shared/stores/sessionStore";
import { useKeepDealerInUrl } from "./shared/lockedDealer";
import { activeVendorNames, startTracking } from "./tracking/bootstrap";
import { useTracking } from "./tracking/useTracking";

const Home = lazy(() => import("./modules/home/pages/HomePage"));
const Start = lazy(() => import("./modules/home/pages/StartPage"));
const Register = lazy(() => import("./modules/home/pages/RegisterPage"));
const GarageConfigurator = lazy(() => import("./modules/garage/pages/GarageConfiguratorPage"));
const Calibration = lazy(() => import("./modules/garage/pages/CalibrationPage"));
const UpAndOver = lazy(() => import("./modules/up-and-over/pages/UpAndOverPage"));
const ExteriorDoor = lazy(() => import("./modules/exterior-door/pages/ExteriorDoorPage"));
const Admin = lazy(() => import("./modules/admin/pages/AdminPage"));
const NoAccess = lazy(() => import("@/shared/pages/NoAccess"));
const PrivacyPolicy = lazy(() => import("@/shared/pages/PrivacyPolicy"));
const NotFound = lazy(() => import("@/shared/pages/NotFound"));

export default function App() {
  return (
    <Router root={Layout}>
      {/* The entrance, for everyone: configure without an account, sign in, or register.
          Reachable without signing in, which is why it can be the root. */}
      <Route path="/" component={Start} />
      {/* The dealer/internal dashboard. Was "/" until the start page took that. */}
      <Route path="/dashboard" component={Home} />
      {/* A URL an email can carry: opens registration through MSAL. Both spellings, like
          /personvern and /privacy. */}
      <Route path="/opprett-konto" component={Register} />
      <Route path="/register" component={Register} />
      {/* The configurator — ONE route for every principal. /garage and /configure rendered the same
          component with identical behaviour; nothing ever branched on which one you were on. */}
      <Route path="/configure" component={GarageConfigurator} />
      {/* The up-and-over door: its own page, internal and admin only (the page and the API both gate it). */}
      <Route path="/vippeport" component={UpAndOver} />
      {/* Doors ordered on their own, internal and admin only. A door sold with a garage door is a step
          of the configurator above instead, open to everyone. */}
      <Route path="/dor" component={ExteriorDoor} />
      {/* Admin-only 3D model calibration tooling (the page itself redirects non-admins). */}
      <Route path="/calibration" component={Calibration} />
      {/* Admin area (the page redirects non-admins; every /api/admin route is gated server-side). */}
      <Route path="/admin" component={Admin} />
      <Route path="/no-access" component={NoAccess} />
      {/* Public: linked from the account-created email, so it must open without signing in. */}
      <Route path="/personvern" component={PrivacyPolicy} />
      <Route path="/privacy" component={PrivacyPolicy} />
      {/* Anything else — a stale link, a typo — lands on the start page rather than a blank screen. */}
      <Route path="*" component={NotFound} />
    </Router>
  );
}

function Layout(props: { children?: any }) {
  // Measures route changes, and mirrors session and tenant into the event envelope. Registered
  // during setup, before the mount below, so the envelope knows who this is by the time the
  // tracking layer decides whether it may load anything at all.
  useTracking();

  // A ?dealer= link's parameter stays in the address bar from page to page; see lockedDealer.
  useKeepDealerInUrl();

  // The browser tab names the tenant, not LOBAS. The SERVER already writes this title into the shell
  // it serves (see SpaShell), so the first paint is right and a link preview is too — this is the other
  // half: it re-titles when the UI language is switched after load, and it is the only half that runs
  // in development, where Vite serves index.html and the server fallback never fires.
  //
  // Waits for the label to actually resolve: t() returns the KEY when its module has not arrived, and
  // "app.title - LOBAS® PORTEN" in the tab is worse than the shell's title for the moment it would show.
  createEffect(() => {
    const label = t("app.title");
    const brand = themeState.tenant.name;
    if (!brand || label === "app.title") return;
    document.title = `${label} - ${brand}`;
  });

  // Force dark; load tenant branding (drives --accent) and resolve the session context
  // (reads a ?token= deep-link, then drives capability gating across the app).
  onMount(() => {
    document.documentElement.classList.add("dark");
    // The consent dialog is mounted here, so it renders on every route — including the start page,
    // which is where a first-time visitor meets it. Its strings live in the dashboard module, and
    // only three pages ask for that module; without this the one component a visitor must be able to
    // read falls back to Norwegian for an English visitor, on the very page they land on.
    void loadModule("dashboard");
    loadTenant();
    loadSession();
    // Opens the consent subscription and mirrors the tenant's tracking section in as it arrives.
    // Nothing is loaded and nothing is sent unless a stored decision grants a category.
    startTracking();
  });

  // Pages render their own full-bleed headers (mockup design). The root is a
  // dark passthrough so every route sits on the dark gradient.
  return (
    <div class="flex min-h-screen flex-col bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white">
      {/* Outside the Suspense, so a chunk that fails to load is caught along with anything a page throws.
          Without this, an exception anywhere in a route left the empty root element and nothing else. */}
      <ErrorBoundary fallback={(error, reset) => <RouteError error={error} reset={reset} />}>
        <Suspense fallback={<LoadingSpinner />}>{props.children}</Suspense>
      </ErrorBoundary>
      {/* After the content, never over it: the configurator fills the viewport and a fixed strip
          would sit on the controls. mt-auto holds it at the bottom on pages shorter than the screen. */}
      <SiteFooter />
      <ToastContainer />
      {/* Outside the Suspense boundary: the question is asked whether or not a route has resolved. */}
      <ConsentBanner activeVendors={activeVendorNames()} />
      {/* Also outside Suspense, and for the same reason: the session can lapse on any route, and a
          request that comes back 401 needs to say so wherever the user happens to be. */}
      <SessionExpiredNotice />
      {/* Also outside Suspense. Raised by the dashboard after an update, and reopened from the profile
          menu — which renders on several routes, so the dialog it opens has to exist on all of them.
          Renders nothing until one of the two asks it to. */}
      <WhatsNewDialog />
    </div>
  );
}

/** What a visitor sees instead of a blank page when a route throws: the choice to try again or reload. */
function RouteError(props: { error: unknown; reset: () => void }) {
  console.error("The page could not be rendered:", props.error);
  return (
    <div class="flex flex-col items-center justify-center gap-4 px-6 py-24 text-center">
      <h1 class="text-xl font-semibold text-white">{t("app.errorTitle", "Something went wrong")}</h1>
      <p class="max-w-md text-sm text-zinc-400">
        {t("app.errorHint", "The page could not be shown. Reloading usually helps.")}
      </p>
      <div class="flex gap-3">
        <button
          type="button"
          onClick={() => props.reset()}
          class="rounded-lg border border-white/10 px-5 py-2.5 text-sm text-zinc-300 hover:bg-white/5 hover:text-white"
        >
          {t("app.errorRetry", "Try again")}
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          class="rounded-lg bg-[var(--accent)] px-5 py-2.5 text-sm text-white hover:opacity-90"
        >
          {t("app.errorReload", "Reload the page")}
        </button>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)]" />
    </div>
  );
}
